import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IncludeFragment} from '@github-ui/include-fragment-react'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {CommandPaletteIcon, SearchIcon} from '@primer/octicons-react'
import {ButtonGroup, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef, useState} from 'react'

import {NavButtonItem} from '../../primitives/NavItemLink'
import {shouldHideTopNav, useTopNavContext} from '../../primitives/TopNav'
import styles from './Search.module.css'

export type SearchProps = {
  show?: boolean
  searchHotkey?: string
  showCommandPalette?: boolean
  isSearchPage?: boolean
  isJumpToSearch?: boolean
}

function SearchPlaceholder() {
  return (
    <span className={clsx(styles.placeholder, styles.text, styles.value)}>
      Type <kbd className={styles.kbd}>/</kbd> to search
    </span>
  )
}

export function Search({show, isJumpToSearch, searchHotkey, showCommandPalette, isSearchPage}: SearchProps) {
  const {searchOpen, setSearchOpen, getFragmentsPath, setTopNavHidden, topNavHidden} = useTopNavContext()
  const [searchTextBlocks, setSearchTextBlocks] = useState<SearchTextBlock[]>([])

  useEffect(() => {
    const urlParams = new URLSearchParams(ssrSafeLocation.search)

    const hideTopNav =
      shouldHideTopNav(urlParams, isSearchPage ?? false, isJumpToSearch ?? false) && !searchTextBlocks.length

    if (topNavHidden !== hideTopNav) {
      setTopNavHidden(hideTopNav)
    }
  }, [setTopNavHidden, topNavHidden, isSearchPage, isJumpToSearch, searchTextBlocks])

  if (!show) {
    return null
  }

  if (isJumpToSearch) {
    return <JumpToSearch showCommandPalette />
  }

  return (
    <>
      <ButtonGroup
        className={clsx(styles.searchButtonGroup, isSearchPage && styles.searchPage, searchOpen && styles.hideButtons)}
      >
        <NavButtonItem
          className={styles.searchButton}
          onClick={() => {
            setSearchOpen(true)
          }}
          icon={SearchIcon}
          analyticsKey="SEARCH"
          aria-label="Search or jump to…"
          linkText={
            searchTextBlocks && searchTextBlocks.length > 0 ? (
              searchTextBlocks.map((block, index) => {
                return (
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <span key={`value-${index}`} className={clsx(styles.text, styles[block.type])}>
                    {block.value}
                  </span>
                )
              })
            ) : (
              <SearchPlaceholder />
            )
          }
        />
        {showCommandPalette && (
          <NavButtonItem
            className="js-activate-command-palette"
            icon={CommandPaletteIcon}
            aria-label="Command palette"
            analyticsKey="COMMAND_PALETTE"
          />
        )}
      </ButtonGroup>
      <NavButtonItem
        icon={SearchIcon}
        aria-label="Search or jump to…"
        aria-expanded={searchOpen}
        aria-haspopup="dialog"
        hotkey={searchHotkey}
        onClick={() => {
          setSearchOpen(true)
        }}
        className={styles.smallSearchButton}
        analyticsKey="SEARCH"
      />
      {isFeatureEnabled('global_nav_react_blackbird_fragment') ? (
        <SearchFragment
          open={searchOpen}
          fragmentsPath={getFragmentsPath('blackbird_search')}
          onClose={closed => {
            setSearchOpen(!closed)
          }}
          onSearchBlocksChange={setSearchTextBlocks}
        />
      ) : (
        <EmbeddedRailsSearch
          open={searchOpen}
          onClose={closed => {
            setSearchOpen(!closed)
          }}
          onSearchBlocksChange={setSearchTextBlocks}
        />
      )}
    </>
  )
}

function JumpToSearch({showCommandPalette}: SearchProps) {
  const {getFragmentsPath} = useTopNavContext()
  const fragmentRef = useRef<HTMLElement | null>(null)

  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const fragment = fragmentRef.current

    if (fragment) {
      const replacedCallback = () => {
        setLoaded(true)
      }

      fragment.addEventListener('include-fragment-replaced', replacedCallback)

      return () => {
        fragment.removeEventListener('include-fragment-replaced', replacedCallback)
      }
    }
  }, [setLoaded])

  return (
    <>
      <span
        className={clsx({
          'AppHeader-search': true,
          'd-none': !loaded,
        })}
      >
        <IncludeFragment ref={fragmentRef} src={getFragmentsPath('search')} accept="text/fragment+html" />
      </span>
      {!loaded && (
        <span className="AppHeader-search">
          <ButtonGroup className={styles.placeholderButtonGroup}>
            <NavButtonItem
              className={clsx(styles.searchButton, styles.placeholderButton)}
              aria-busy
              icon={SearchIcon}
              analyticsKey="SEARCH"
              aria-label="Search or jump to…"
              linkText={<SearchPlaceholder />}
            />
            {showCommandPalette && (
              <NavButtonItem
                className="js-activate-command-palette"
                icon={CommandPaletteIcon}
                aria-label="Command palette"
                analyticsKey="COMMAND_PALETTE"
              />
            )}
          </ButtonGroup>
          <IconButton
            icon={SearchIcon}
            aria-label="Search or jump to…"
            aria-busy
            className={clsx(styles.mobileSearch, styles.mobilePlaceholderButton)}
          />
        </span>
      )}
    </>
  )
}

function SearchFragment(props: {
  open?: boolean
  fragmentsPath?: string | undefined
  onClose?: (closed: boolean) => void
  onSearchBlocksChange?: (blocks: SearchTextBlock[]) => void
}) {
  const searchInputWrapperRef = useRef<HTMLDivElement | null>(null)
  const fragmentRef = useRef<HTMLElement | null>(null)
  const [loaded, setLoaded] = useState(false)

  useSearchElement(searchInputWrapperRef, {
    loaded,
    onClose: props.onClose,
    open: props.open,
    onSearchBlocksChange: props.onSearchBlocksChange,
  })

  useEffect(() => {
    const fragment = fragmentRef.current

    if (fragment) {
      const replacedCallback = () => {
        setLoaded(true)
      }

      fragment.addEventListener('include-fragment-replaced', replacedCallback)

      return () => {
        fragment.removeEventListener('include-fragment-replaced', replacedCallback)
      }
    }
  }, [setLoaded])

  return (
    <div
      className={clsx({
        'd-flex width-full': props.open,
        'd-none': !props.open,
      })}
      ref={searchInputWrapperRef}
    >
      <IncludeFragment ref={fragmentRef} loading="eager" src={props.fragmentsPath} accept="text/fragment+html" />
    </div>
  )
}

function EmbeddedRailsSearch(props: {
  open?: boolean
  onClose?: (closed: boolean) => void
  onSearchBlocksChange?: (blocks: SearchTextBlock[]) => void
}) {
  const searchInputWrapperRef = useRef<HTMLDivElement | null>(null)

  const [loaded, setLoaded] = useState(false)

  useLayoutEffect(() => {
    // Wait until the DOM is attached to mark as loaded
    setLoaded(true)
  }, [])

  const onInitialize = useCallback(() => {
    const searchInput = document.querySelector('qbsearch-input')
    if (!searchInput || !isSearchInputElement(searchInput)) {
      return
    }

    searchInputWrapperRef.current?.appendChild(searchInput)
    const suggestionsTokenInput = document.querySelector('.js-data-jump-to-suggestions-path-csrf')
    if (suggestionsTokenInput) {
      searchInputWrapperRef.current?.appendChild(suggestionsTokenInput)
    }
  }, [])

  useSearchElement(searchInputWrapperRef, {
    loaded,
    onClose: props.onClose,
    open: props.open,
    onSearchBlocksChange: props.onSearchBlocksChange,
    onInitialize,
  })

  return (
    <div
      className={clsx({
        'd-flex width-full': props.open,
        'd-none': !props.open,
      })}
      ref={searchInputWrapperRef}
    />
  )
}

export function MobileSearch({show, isJumpToSearch}: SearchProps) {
  const {hasLocalNavigation, getFragmentsPath} = useTopNavContext()

  return (
    show &&
    isJumpToSearch && (
      <div
        className={clsx(
          'js-global-bar-second-row',
          styles.mobileSearchContainer,
          hasLocalNavigation && styles.withLocalNav,
        )}
        hidden
      >
        <div className={clsx('AppHeader-search', styles.mobileSearch)}>
          <IncludeFragment src={getFragmentsPath('mobile_search')} accept="text/fragment+html" />
        </div>
      </div>
    )
  )
}

export function CommandPalette({show, showCommandPalette}: SearchProps) {
  const {getFragmentsPath} = useTopNavContext()

  if (!show || !showCommandPalette || !isFeatureEnabled('global_nav_react_blackbird_fragment')) {
    return null
  }

  return <IncludeFragment src={getFragmentsPath('command_palette')} accept="text/fragment+html" />
}

/**
 * A partial definition of the qbsearch-input web component's element interface
 */
export type SearchInputElement = Element & {
  handleExpand: () => void
  closeDialog: () => void
  syncRichButtonText: () => void
}

function isSearchInputElement(element: Element | null): element is SearchInputElement {
  return element !== null && 'handleExpand' in element && 'closeDialog' in element && 'syncRichButtonText' in element
}

/**
 * A block of text in the search input, representing either a filter, constant text, entity, or value
 */
export type SearchTextBlock = {
  type: 'filter' | 'constant' | 'entity' | 'value'
  value: string
}

function useSearchElement(
  containerRef: React.RefObject<HTMLElement | null>,
  options: {
    loaded?: boolean
    open?: boolean
    onClose?: (closed: boolean) => void
    onSearchBlocksChange?: (blocks: SearchTextBlock[]) => void
    onInitialize?: () => void
  },
) {
  const searchInputRef = useRef<SearchInputElement | null>(null)
  const {onInitialize} = options
  const container = containerRef.current

  const initializeSearch = useCallback(() => {
    if (!options.loaded || !container || searchInputRef.current) {
      return
    }

    onInitialize?.()

    // Move the search input into the center section of the navigation bar
    const searchInput = container.querySelector('qbsearch-input')
    if (!searchInput || !isSearchInputElement(searchInput)) {
      return
    }

    searchInputRef.current = searchInput
    searchInputRef.current.setAttribute('hidden', 'true')

    searchInputRef.current.syncRichButtonText()
  }, [container, options.loaded, onInitialize])

  const openSearch = useCallback(() => {
    initializeSearch()
    searchInputRef.current?.handleExpand()
  }, [initializeSearch])

  const onCloseSearch = useCallback(() => {
    searchInputRef.current?.closeDialog()
    options.onClose?.(true)
  }, [options])

  const onOpenSearch = useCallback(() => {
    searchInputRef.current?.removeAttribute('hidden')
    options.onClose?.(false)
  }, [options])

  const onUpdateText = useCallback(
    (event: Event) => {
      if (!(event instanceof CustomEvent)) return
      options.onSearchBlocksChange?.(event.detail.searchTextBlocks as SearchTextBlock[])
    },
    [options],
  )

  useEffect(() => {
    if (options.open) {
      openSearch()
    }
  }, [options.open, openSearch])

  useEffect(() => {
    // Initialize search input on mount
    initializeSearch()

    document.addEventListener('qbsearch-input:close', onCloseSearch)
    document.addEventListener('qbsearch-input:expand', onOpenSearch)
    document.addEventListener('qbsearch-input:updateText', onUpdateText)

    return () => {
      document.removeEventListener('qbsearch-input:close', onCloseSearch)
      document.removeEventListener('qbsearch-input:expand', onOpenSearch)
      document.removeEventListener('qbsearch-input:updateText', onUpdateText)
    }
  }, [onCloseSearch, onOpenSearch, onUpdateText, initializeSearch])
}

try{ SearchPlaceholder.displayName ||= 'SearchPlaceholder' } catch {}
try{ Search.displayName ||= 'Search' } catch {}
try{ JumpToSearch.displayName ||= 'JumpToSearch' } catch {}
try{ SearchFragment.displayName ||= 'SearchFragment' } catch {}
try{ EmbeddedRailsSearch.displayName ||= 'EmbeddedRailsSearch' } catch {}
try{ MobileSearch.displayName ||= 'MobileSearch' } catch {}
try{ CommandPalette.displayName ||= 'CommandPalette' } catch {}